import { useEffect, useState } from 'react';
import {
  PaperClipIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { fileService } from '../../clients/ApiClients';
import { AttachmentReturn } from '../../clients/rust-api-client';
import { savePdfResponse } from '../../utils/billingHelpers';
import {
  AttachmentError,
  cachedFailure,
  cachedUrl,
  loadInlineUrl,
} from './attachmentCache';
import { formatFileSize, isImageAttachment } from './attachmentRules';

interface Props {
  attachment: AttachmentReturn;
  onRemove?: (attachment: AttachmentReturn) => void;
}

const failureText = (reason: AttachmentError): string => {
  switch (reason) {
    case 'not-found':
      return 'This file is no longer available.';
    case 'not-renderable':
      return 'Preview unavailable.';
    default:
      return 'Preview unavailable.';
  }
};

export function AttachmentThumb({ attachment, onRemove }: Props) {
  const showAsImage = isImageAttachment(attachment);

  const [url, setUrl] = useState<string | undefined>(() =>
    cachedUrl(attachment.file_id)
  );
  const [error, setError] = useState<AttachmentError | undefined>(() =>
    cachedFailure(attachment.file_id)
  );
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    // Only pictures are fetched eagerly. A PDF or a heic costs nothing
    // until somebody asks for it, which is most of them.
    if (!showAsImage || url || error) return;

    let cancelled = false;
    loadInlineUrl(attachment.file_id)
      .then((next) => {
        if (!cancelled) setUrl(next);
      })
      .catch((reason: AttachmentError) => {
        if (!cancelled) setError(reason);
      });

    return () => {
      cancelled = true;
    };
    // The object URL is owned by the cache, not by this effect, so there
    // is deliberately no revoke in the cleanup.
  }, [attachment.file_id, showAsImage, url, error]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fileService.downloadFile(
        { id: attachment.file_id },
        { responseType: 'blob' as const }
      );
      savePdfResponse(response, attachment.file_name);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setError('failed');
    } finally {
      setDownloading(false);
    }
  };

  const removeButton = onRemove && (
    <button
      type="button"
      aria-label={`Remove ${attachment.file_name}`}
      onClick={() => onRemove(attachment)}
      className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-gray-600 shadow hover:text-gray-900 dark:bg-gray-800/90 dark:text-gray-300 dark:hover:text-white"
    >
      <XMarkIcon className="h-4 w-4" />
    </button>
  );

  // A picture we have the bytes for. Clicking opens it full size on the
  // same object URL, so there is no second fetch.
  if (showAsImage && url && !error) {
    return (
      <div className="relative">
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img
            src={url}
            alt={attachment.file_name}
            className="h-28 w-28 rounded-lg object-cover ring-1 ring-zinc-200 dark:ring-zinc-700"
          />
        </a>
        {removeButton}
      </div>
    );
  }

  if (showAsImage && !url && !error) {
    return (
      <div className="h-28 w-28 animate-pulse rounded-lg bg-zinc-200 dark:bg-zinc-700" />
    );
  }

  // Everything else: a PDF, a spreadsheet, a heic the browser will not
  // paint, or a picture whose bytes did not arrive. A broken-image glyph
  // is never the answer.
  return (
    <div className="relative flex items-center gap-2 rounded-lg px-3 py-2 ring-1 ring-zinc-200 dark:ring-zinc-700">
      <PaperClipIcon className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" />
      <div className="min-w-0">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || error === 'not-found'}
          className="block max-w-[14rem] truncate text-left text-sm text-sky-600 hover:underline disabled:text-gray-500 disabled:no-underline dark:text-sky-400 dark:disabled:text-gray-400"
        >
          {downloading ? 'Downloading…' : attachment.file_name}
        </button>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatFileSize(attachment.file_size)}
          {error ? ` · ${failureText(error)}` : ''}
        </span>
      </div>
      {!error && (
        <ArrowDownTrayIcon className="h-4 w-4 shrink-0 text-gray-400" />
      )}
      {removeButton}
    </div>
  );
}

export default AttachmentThumb;
